import { DatePickerPlacements, StandardDatePicker } from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, FormEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useFetch } from '@api/ApiHelper';
import { ApiDatumResponse } from '@api/v4/ApiResponseTypes';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';
import { PrimaryButton } from '@components/library/button';
import { DialogSizes, StandardDialog } from '@components/library/dialog';
import { StandardTextField } from '@components/library/text-field';
import { getStandardDatePickerLabels } from '@translations';

import classes from '../styles/create-api-key-modal.module.scss';

const KEY_NAME_MIN_LENGTH = 3;
const KEY_NAME_MAX_LENGTH = 64;
const KEY_NAME_PATTERN = /^[A-Za-z0-9 _-]+$/;
const DESCRIPTION_MAX_LENGTH = 256;
const COPY_FEEDBACK_DURATION_MS = 2000;

type ModalStep = 'form' | 'generated';

interface CreateApiKeyModalProps {
  onCreated?: () => void;
  open: boolean;
  organizationKey: string;
  setOpen: (open: boolean) => void;
}

interface CreateApiKeyFormValues {
  description: string;
  expiryDate: string;
  name: string;
}

interface CreateApiKeyCreatedByUser {
  email: string;
  id: number;
  name: string;
  user_key: string;
}

/** JSON:API attributes from POST /api/v4/organizations/:key/api_keys */
interface CreateApiKeyAttributes {
  created_at: string;
  created_by_user: CreateApiKeyCreatedByUser;
  description: string | null;
  expires_at: string;
  last_used_at: string | null;
  name: string;
  revoked_at: string | null;
  rotated_from: string | null;
  source: string;
  status: string;
  token: string;
  token_prefix: string;
}

type CreateApiKeyResponse = ApiDatumResponse<CreateApiKeyAttributes, 'api_key'>;

interface CreateApiKeyRequestBody {
  description?: string;
  expires_at?: string;
  name: string;
}

const initialValues: CreateApiKeyFormValues = {
  description: '',
  expiryDate: '',
  name: '',
};

/** Match API sample format: 2026-12-01T00:00:00Z using the picked calendar day. */
const toExpiresAt = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00Z`;
};

export const CreateApiKeyModal: FunctionComponent<CreateApiKeyModalProps> = ({
  onCreated,
  open,
  organizationKey,
  setOpen,
}) => {
  const [values, setValues] = useState<CreateApiKeyFormValues>(initialValues);
  const [nameError, setNameError] = useState<string>('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [step, setStep] = useState<ModalStep>('form');
  const [generatedToken, setGeneratedToken] = useState('');
  const [hasCopiedOnce, setHasCopiedOnce] = useState(false);
  const [isCopyFeedbackVisible, setIsCopyFeedbackVisible] = useState(false);
  const expiryInputRef = useRef<HTMLInputElement>(null);
  const copyFeedbackTimeoutRef = useRef<number | null>(null);
  const expiryPlaceholder = t`Expires in 5 years by default`;

  const createApiKeyOptions = {
    fetchOnMount: false,
    method: 'POST' as const,
    url: `/api/v4/organizations/${organizationKey}/api_keys`,
  };

  const createApiKey = useFetch<CreateApiKeyResponse>(createApiKeyOptions);
  const isCreating = createApiKey.loading;

  const clearCopyFeedbackTimeout = (): void => {
    if (copyFeedbackTimeoutRef.current !== null) {
      window.clearTimeout(copyFeedbackTimeoutRef.current);
      copyFeedbackTimeoutRef.current = null;
    }
  };

  const showGeneratedKey = (name: string, token: string): void => {
    clearCopyFeedbackTimeout();
    setGeneratedToken(token);
    setStep('generated');
    setHasCopiedOnce(false);
    setIsCopyFeedbackVisible(false);

    Notify.create({
      title: t`API Key ${name} created successfully`,
      type: 'success',
    });
    onCreated?.();
  };

  const resetModalState = (): void => {
    clearCopyFeedbackTimeout();
    setValues(initialValues);
    setNameError('');
    setSubmitAttempted(false);
    setStep('form');
    setGeneratedToken('');
    setHasCopiedOnce(false);
    setIsCopyFeedbackVisible(false);
    createApiKey.reset();
  };

  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => (): void => {
    clearCopyFeedbackTimeout();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // StandardDatePicker hardcodes locale placeholders (e.g. mm/dd/yyyy); override for this field.
  useLayoutEffect(() => {
    if (!open || step !== 'form') return;

    const input = expiryInputRef.current;
    if (!input) return;

    const applyPlaceholder = (): void => {
      if (input.getAttribute('placeholder') !== expiryPlaceholder) {
        input.setAttribute('placeholder', expiryPlaceholder);
      }
    };

    applyPlaceholder();
    const observer = new MutationObserver(applyPlaceholder);
    observer.observe(input, { attributes: true, attributeFilter: ['placeholder'] });

    return (): void => observer.disconnect();
  }, [open, step, expiryPlaceholder]);

  useEffect(() => {
    if (!createApiKey.response) return;
    const attributes = createApiKey.response?.data?.attributes;
    if (!attributes?.token) return;

    showGeneratedKey(attributes.name, attributes.token);
    // Clear fetch state so Strict Mode / re-renders don't re-handle the same response.
    createApiKey.reset();
  }, [createApiKey.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!createApiKey.error) return;

    Notify.create({
      body: createApiKey.error.message || t`Please try again or contact support`,
      title: t`Error creating API Key`,
      type: 'error',
    });
  }, [createApiKey.error]);

  const validateName = (name: string): string => {
    const trimmed = name.trim();
    if (!trimmed) {
      return t`Name is required`;
    }
    if (trimmed.length < KEY_NAME_MIN_LENGTH) {
      return t`Name must be at least 3 characters`;
    }
    if (!KEY_NAME_PATTERN.test(trimmed)) {
      return t`Name can only contain letters, numbers, hyphens, underscores, and spaces`;
    }
    return '';
  };

  const isSubmitDisabled = !values.name.trim() || !!validateName(values.name) || isCreating;

  const handleNameChange = (e: InputChangeEvent | TextAreaChangeEvent): void => {
    const name = e.target.value;
    setValues((prev) => ({ ...prev, name }));

    if (submitAttempted) {
      setNameError(validateName(name));
      return;
    }

    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t`Name is required`);
    } else if (!KEY_NAME_PATTERN.test(trimmed)) {
      setNameError(t`Name can only contain letters, numbers, hyphens, underscores, and spaces`);
    } else {
      setNameError('');
    }
  };

  const handleDescriptionChange = (e: InputChangeEvent | TextAreaChangeEvent): void => {
    const description = e.target.value;
    setValues((prev) => ({ ...prev, description }));
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setSubmitAttempted(true);

    const error = validateName(values.name);
    setNameError(error);
    if (error) {
      return;
    }

    const body: CreateApiKeyRequestBody = {
      name: values.name.trim(),
      ...(values.description.trim() && { description: values.description.trim() }),
      ...(values.expiryDate && { expires_at: values.expiryDate }), // eslint-disable-line @typescript-eslint/naming-convention
    };

    createApiKey.fetch({
      ...createApiKeyOptions,
      body,
    });
  };

  const handleCopy = async (): Promise<void> => {
    if (!generatedToken) return;

    try {
      if (window.navigator?.clipboard) {
        await window.navigator.clipboard.writeText(generatedToken);
      } else {
        const textField = document.createElement('textarea');
        textField.value = generatedToken;
        document.body.appendChild(textField);
        textField.select();
        document.execCommand('copy');
        textField.remove();
      }
      setHasCopiedOnce(true);
      setIsCopyFeedbackVisible(true);
      clearCopyFeedbackTimeout();
      copyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setIsCopyFeedbackVisible(false);
        copyFeedbackTimeoutRef.current = null;
      }, COPY_FEEDBACK_DURATION_MS);
    } catch {
      Notify.create({
        title: t`Unable to copy API key`,
        type: 'error',
      });
    }
  };

  const handleDone = (): void => {
    if (!hasCopiedOnce) return;
    setOpen(false);
  };

  const isGeneratedStep = step === 'generated';

  return (
    <StandardDialog
      dialogBodyClassName={classes.createApiKeyModalBody}
      dialogHeaderClassName={classes.createApiKeyModalHeader}
      id="create-api-key-modal"
      open={open}
      setOpen={setOpen}
      showFooter={false}
      size={DialogSizes.Auto}
      title={isGeneratedStep ? t`New API Key Generated` : t`Create API Key`}
      width={520}
    >
      {isGeneratedStep ? (
        <div className={classes.generatedContent}>
          <p className={classes.securityNotice}>
            <Trans>
              Copy this key and store it securely now. This is the only time it will be shown — after you close this
              window, it cannot be viewed or copied again.
            </Trans>
          </p>

          <div className={classes.keyField}>
            <span className={classes.keyLabel} id="generated-api-key-label">
              <Trans>Key</Trans>
            </span>
            <div className={classes.keyInputRow}>
              <div
                aria-labelledby="generated-api-key-label"
                className={classes.keyInput}
                id="generated-api-key"
                onCopy={(event): void => event.preventDefault()}
              >
                {generatedToken}
              </div>
              <button
                aria-label={isCopyFeedbackVisible ? t`Copied` : t`Copy API key`}
                className={classes.copyButton}
                onClick={(): void => { void handleCopy(); }}
                type="button"
              >
                {isCopyFeedbackVisible ? (
                  <>
                    <span aria-hidden="true" className={classes.copiedCheck}>✓</span>
                    <Trans>Copied</Trans>
                  </>
                ) : (
                  <Trans>Copy</Trans>
                )}
              </button>
            </div>
          </div>

          <div className={classes.actions}>
            <PrimaryButton
              className={classes.doneButton}
              disabled={!hasCopiedOnce}
              onClick={handleDone}
              size="small"
              type="button"
            >
              <Trans>Done</Trans>
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <form className={classes.form} onSubmit={handleSubmit}>
          <p className={classes.securityNotice}>
            <Trans>
              API keys provide privileged access to data in your Brandfolder Account.
              Never share your API secret keys in public places like GitHub, frontend code, etc.
            </Trans>
          </p>

          <StandardTextField
            className={classes.textField}
            error={nameError}
            id="api-key-name"
            label={<Trans>Key Name</Trans>}
            maxLength={KEY_NAME_MAX_LENGTH}
            name="name"
            onChange={handleNameChange}
            placeholder={t`Input Key Name`}
            required
            value={values.name}
          />

          <StandardTextField
            className={classes.textField}
            id="api-key-description"
            label={<Trans>Description</Trans>}
            maxLength={DESCRIPTION_MAX_LENGTH}
            name="description"
            onChange={handleDescriptionChange}
            placeholder={t`Optional`}
            value={values.description}
          />

          <div className={classes.expiryDateField}>
            <StandardDatePicker
              ref={expiryInputRef}
              allowPastDates={false}
              id="api-key-expiry-date"
              labels={getStandardDatePickerLabels({
                chooseDate: expiryPlaceholder,
                label: t`Expiry Date`,
              })}
              locale={determineUGTLocale()}
              onSelection={(date): void => {
                setValues((prev) => ({
                  ...prev,
                  expiryDate: toExpiresAt(date),
                }));
              }}
              showLabel
              useLayerOptions={{
                placement: DatePickerPlacements.BottomStart,
              }}
            />
          </div>

          <div className={classes.actions}>
            <PrimaryButton
              className={classes.submitButton}
              disabled={isSubmitDisabled}
              isLoading={isCreating}
              loadingCopy={t`Creating...`}
              size="medium"
              type="submit"
            >
              <Trans>Create New Key</Trans>
            </PrimaryButton>
          </div>
        </form>
      )}
    </StandardDialog>
  );
};
